import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createMdsWorksheet,
  getMdsWorksheetAggregate,
  getMdsWorksheetAnchors,
  MdsWorksheetAggregate,
  MdsWorksheetAnchor,
} from 'src/services/documentService/mdsWorksheet';

/**
 * BRK-1852: worksheet page state — loads the patient's assessment anchors,
 * auto-selects the most recent (the backend returns them effective-ARD
 * descending), loads its aggregate, and supports switching anchors and
 * creating a new one. Stale responses dropped via a request counter.
 */
export function useMdsWorksheet(patientUuid: string | undefined) {
  const [anchors, setAnchors] = useState<MdsWorksheetAnchor[] | null>(null);
  // generation-keyed: same-uuid re-selection must still re-run the aggregate
  // effect (a bare string state would bail out on identical values and leave
  // the loading spinner stuck forever)
  const [selection, setSelection] = useState<{ uuid: string | null; gen: number }>({
    uuid: null,
    gen: 0,
  });
  const [aggregate, setAggregate] = useState<MdsWorksheetAggregate | null>(null);
  const [loading, setLoading] = useState(true);
  // a post-submit reload of the SAME anchor: keep the current table on screen
  // and gate re-submit (a stale baseVersionNumber would defeat conflict
  // detection) instead of blanking to a spinner
  const [refreshing, setRefreshing] = useState(false);
  const isRefreshRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  const loadAnchors = useCallback(async (selectUuid?: string) => {
    if (!patientUuid) {
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await getMdsWorksheetAnchors(patientUuid);
      if (seq !== requestSeq.current) {
        return;
      }
      setAnchors(result);
      const target = selectUuid || result[0]?.assessmentUuid || null;
      setSelection((prev) => ({ uuid: target, gen: prev.gen + 1 }));
      if (!target) {
        setAggregate(null);
        setLoading(false);
      }
    } catch (e: any) {
      if (seq === requestSeq.current) {
        setError(e.message);
        setAnchors(null);
        setAggregate(null);
        setLoading(false);
      }
    }
  }, [patientUuid]);

  useEffect(() => {
    loadAnchors();
  }, [loadAnchors]);

  useEffect(() => {
    if (!selection.uuid) {
      return;
    }
    const seq = ++requestSeq.current;
    // a refresh (reloadAggregate) keeps the stale table visible; an initial
    // load / anchor switch blanks to the spinner
    const isRefresh = isRefreshRef.current;
    isRefreshRef.current = false;
    if (!isRefresh) {
      setLoading(true);
    }
    setError(null);
    getMdsWorksheetAggregate(selection.uuid)
      .then((result) => {
        if (seq === requestSeq.current) {
          setAggregate(result);
          setLoading(false);
          setRefreshing(false);
        }
      })
      .catch((e: Error) => {
        if (seq === requestSeq.current) {
          setError(e.message);
          // keep the current worksheet on a failed refresh; only blank on a
          // failed initial/anchor-switch load
          if (!isRefresh) {
            setAggregate(null);
          }
          setLoading(false);
          setRefreshing(false);
        }
      });
  }, [selection]);

  const selectAnchor = useCallback((assessmentUuid: string) => {
    setSelection((prev) => ({ uuid: assessmentUuid, gen: prev.gen + 1 }));
  }, []);

  // re-fetch the current anchor's aggregate in place (after a submit): bumping
  // the generation re-runs the aggregate effect without changing the uuid
  const reloadAggregate = useCallback(() => {
    isRefreshRef.current = true;
    setRefreshing(true);
    setSelection((prev) => ({ ...prev, gen: prev.gen + 1 }));
  }, []);

  const createWorksheet = useCallback(
    async (assessmentType: string, ard: string): Promise<void> => {
      if (!patientUuid) {
        return;
      }
      // errors propagate to the dialog for inline display
      const created = await createMdsWorksheet({ patientUuid, assessmentType, ard });
      await loadAnchors(created.assessmentUuid);
    },
    [patientUuid, loadAnchors]
  );

  return {
    anchors,
    selectedAnchor: selection.uuid,
    aggregate,
    loading,
    refreshing,
    error,
    selectAnchor,
    createWorksheet,
    reloadAggregate,
  };
}
